import type { BoardPost } from '@talkofthetown/shared';
import { useEffect, useRef, useState, type CSSProperties } from 'react';
import { useMediaQuery } from '../hooks/misc';
import { READ_MORE, timeAgo } from '../lib/format';
import { nextWhisper, stillWhispers, type WhisperTurns } from '../lib/whispers';

/** A box on the board, in % of its width and height. */
export interface Box {
  x: number;
  y: number;
  w: number;
  h: number;
}

interface Props {
  /** New and faded posts (and voices too quiet for the middle), newest first. */
  whispers: BoardPost[];
  /** Paid posts: yellow, marked Sponsored, on screen longer and more often (see lib/whispers). */
  sponsored: BoardPost[];
  now: number;
  /**
   * Where the voices are: the whispers drift all around it. Without one (no voices yet) they take
   * the whole board, under `note`.
   */
  around?: Box;
  /** A place around the voices kept for the ad: whispers keep out of it. */
  reserved?: Box;
  note?: string;
  onOpen: (post: BoardPost) => void;
}

interface Drifting {
  key: number;
  post: BoardPost;
  slot: number;
}

/** A new whisper starts this often (ms) while there's room; each lives for about 6.5 s (13 s
 * sponsored, as in styles.css). */
const EVERY_MS = 1200;

/** The places around the voices a whisper can appear: columns either side, bands above and below. */
function ringSlots(box: Box): Box[] {
  const slots: Box[] = [];
  if (box.x >= 14) {
    for (let i = 0; i < 3; i++) {
      slots.push({ x: 0.5, y: i * 33.3 + 1, w: box.x - 2, h: 31.3 });
      slots.push({ x: box.x + box.w + 1.5, y: i * 33.3 + 1, w: 100 - box.x - box.w - 2, h: 31.3 });
    }
  }
  if (box.y >= 12) {
    for (let j = 0; j < 2; j++) {
      const x = box.x + (j * box.w) / 2 + 0.5;
      slots.push({ x, y: 0.5, w: box.w / 2 - 1, h: box.y - 1.5 });
      // Below: a little further off, as whispers rise while they fade.
      slots.push({ x, y: box.y + box.h + 3, w: box.w / 2 - 1, h: 100 - box.y - box.h - 3.5 });
    }
  }
  return slots;
}

/** The whole board in cells (no voices yet). */
function gridSlots(cols: number, rows: number): Box[] {
  return Array.from({ length: cols * rows }, (_, i) => ({
    x: (i % cols) * (100 / cols) + 1,
    y: Math.floor(i / cols) * (100 / rows) + 1,
    w: 100 / cols - 2,
    h: 100 / rows - 2,
  }));
}

function Whisper({
  post,
  now,
  style,
  onOpen,
  onDone,
}: {
  post: BoardPost;
  now: number;
  style?: CSSProperties;
  onOpen: (post: BoardPost) => void;
  onDone?: () => void;
}) {
  return (
    <button
      type="button"
      className={`whisper${post.sponsored ? ' whisper--sponsored' : ''}`}
      style={style}
      data-post-id={post.id}
      data-tail={['bl', 'br', 'tl', 'tr'][(post.id.charCodeAt(0) + post.id.length) % 4]}
      title={READ_MORE}
      aria-label={`${post.sponsored ? 'Sponsored: ' : ''}${post.title}, ${post.isAgent ? `AI summary of ${post.author}` : `by ${post.author}`}, ${timeAgo(post.createdAt, now)}. Open it to give it a voice`}
      onClick={() => onOpen(post)}
      onAnimationEnd={(e) => e.target === e.currentTarget && onDone?.()}
    >
      <span className="whisper__title">{post.title}</span>
      <span className="whisper__meta">
        {post.sponsored && <span className="badge badge--sponsored">Sponsored</span>}
        {post.isAgent && <span title="AI summary">✦ </span>}
        {post.author} · {timeAgo(post.createdAt, now)}
      </span>
    </button>
  );
}

const place = (slot: Box): CSSProperties => ({
  left: `${slot.x}%`,
  top: `${slot.y}%`,
  maxWidth: `${slot.w}%`,
});

/**
 * Whispers: posts nobody has opened yet (and ones that have faded). They appear and disappear all
 * around the voices, a few at a time; opening one is what gives it a voice. The voices never move;
 * only the whispers do. With reduced motion they sit still.
 */
export function Whispers({ whispers, sponsored, now, around, reserved, note, onOpen }: Props) {
  const still = useMediaQuery('(prefers-reduced-motion: reduce)');
  const phone = useMediaQuery('(max-width: 640px)');
  const overlaps = (a: Box, b: Box) =>
    a.x < b.x + b.w && b.x < a.x + a.w && a.y < b.y + b.h && b.y < a.y + a.h;
  const slots = (around ? ringSlots(around) : gridSlots(phone ? 2 : 3, phone ? 4 : 3)).filter(
    (s) => !reserved || !overlaps(s, reserved),
  );
  const slotKey = slots.map((s) => `${s.x.toFixed(1)},${s.y.toFixed(1)}`).join('|');
  // Leave a couple of places empty, so where the next one appears stays a surprise.
  const room = Math.min(Math.max(1, slots.length - 2), whispers.length + sponsored.length);
  const [drifting, setDrifting] = useState<Drifting[]>([]);
  const next = useRef<{ turns: WhisperTurns; key: number }>({
    turns: { whisper: 0, sponsored: 0 },
    key: 0,
  });
  const latest = useRef({ whispers, sponsored });
  const drift = useRef<Drifting[]>([]);
  useEffect(() => {
    latest.current = { whispers, sponsored };
  }, [whispers, sponsored]);

  useEffect(() => {
    if (still || room === 0) return;
    const count = slotKey.split('|').length;
    const add = () => {
      const now = drift.current.filter((d) => d.slot < count);
      if (now.length >= room) return;
      const used = new Set(now.map((d) => d.slot));
      const free = Array.from({ length: count }, (_, i) => i).filter((s) => !used.has(s));
      // The next whisper not already showing (sponsored ones first, up to their share).
      const { whispers: list, sponsored: paid } = latest.current;
      const pick = nextWhisper(
        list,
        paid,
        now.map((d) => d.post),
        room,
        next.current.turns,
      );
      if (!pick || free.length === 0) return;
      const post = pick.post;
      next.current.turns = pick.turns;
      const key = ++next.current.key;
      drift.current = [...now, { key, post, slot: free[(key * 7) % free.length]! }];
      setDrifting(drift.current);
    };
    add();
    const id = window.setInterval(add, EVERY_MS);
    return () => window.clearInterval(id);
  }, [still, room, slotKey]);

  const done = (key: number) => {
    drift.current = drift.current.filter((d) => d.key !== key);
    setDrifting(drift.current);
  };

  const air = (
    <div className="whispers__air">
      {still
        ? stillWhispers(whispers, sponsored, slots.length).map((w, i) => (
            <Whisper key={w.id} post={w} now={now} onOpen={onOpen} style={place(slots[i]!)} />
          ))
        : drifting.map((d) =>
            slots[d.slot] ? (
              <Whisper
                key={d.key}
                post={d.post}
                now={now}
                onOpen={onOpen}
                onDone={() => done(d.key)}
                style={place(slots[d.slot]!)}
              />
            ) : null,
          )}
    </div>
  );

  if (around) {
    return (
      <div className="whispers whispers--ring" role="region" aria-label="Whispers">
        {air}
      </div>
    );
  }
  return (
    <div className="whispers" role="region" aria-label="Whispers">
      <div className="whispers__head">
        <p>
          {note ??
            `${whispers.length} ${whispers.length === 1 ? 'whisper' : 'whispers'}: new posts nobody has opened yet.`}{' '}
          Open one to give it a voice.
        </p>
      </div>
      {whispers.length + sponsored.length === 0 ? (
        <p className="whispers__none">No whispers right now.</p>
      ) : still ? (
        <div className="whispers__list">
          {stillWhispers(whispers, sponsored, whispers.length + sponsored.length).map((w) => (
            <Whisper key={w.id} post={w} now={now} onOpen={onOpen} />
          ))}
        </div>
      ) : (
        air
      )}
    </div>
  );
}
